/* Product page variant switching (sections/main-product.liquid).
   A change on a [data-variant-select] fetches the section for that variant
   via the Section Rendering API (?variant=ID&section_id=ID) and swaps every
   [data-product-part] (price, stock/delivery, SKU). The quantity field keeps
   its value; only its max (stock limit) is updated. The URL follows the
   selection via history.replaceState. Without JS the forms work natively. */
(() => {
  if (customElements.get('g-product-info')) return;

  class GProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-variant-select]');
        if (select) this.onVariantChange(select);
      }, { signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.request?.abort();
      this.initialized = false;
    }

    onVariantChange(select) {
      const variantId = select.value;
      if (!variantId) return;

      // Quote-only picker: preselect the same variant in the inline form.
      if (select.hasAttribute('data-quote-picker')) {
        const title = select.selectedOptions[0]?.dataset.title;
        this.querySelectorAll('[data-quote-form-variant]').forEach((field) => {
          if (title && [...field.options].some((option) => option.value === title)) field.value = title;
        });
      }

      this.updateUrl(variantId);
      this.render(variantId);
    }

    updateUrl(variantId) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // URL update is cosmetic; ignore failures (e.g. sandboxed previews).
      }
    }

    async render(variantId) {
      this.request?.abort();
      this.request = new AbortController();
      const params = new URLSearchParams({ variant: variantId, section_id: this.dataset.section });
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.request.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`g-product-info[data-section="${CSS.escape(this.dataset.section)}"]`);
        if (fresh) this.swap(fresh);
        this.removeAttribute('aria-busy');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.removeAttribute('aria-busy');
        // Fall back to a full page load so price and stock are never stale.
        window.location.assign(`${this.dataset.url}?variant=${encodeURIComponent(variantId)}`);
      }
    }

    swap(fresh) {
      this.querySelectorAll('[data-product-part]').forEach((part) => {
        const next = fresh.querySelector(`[data-product-part="${part.dataset.productPart}"]`);
        if (next) part.replaceWith(document.importNode(next, true));
      });

      const quantity = this.querySelector('[data-product-quantity]');
      const nextQuantity = fresh.querySelector('[data-product-quantity]');
      if (quantity && nextQuantity) {
        const max = nextQuantity.getAttribute('max');
        if (max) {
          quantity.setAttribute('max', max);
          if (Number(quantity.value) > Number(max)) quantity.value = max;
        } else {
          quantity.removeAttribute('max');
        }
      }
    }
  }

  customElements.define('g-product-info', GProductInfo);
})();
